/**
 * 微信公众号支付（JSAPI）相关接口
 *
 * 对应 Apipost: 推广中心 > 统一确认支付页面
 *
 * 三个接口：
 *   1. GET  /home/wechat-pay/order-info       — 获取统一确认支付页面信息
 *   2. POST /home/wechat-pay/jsapi/pay         — 发起支付（form-data）
 *   3. GET  /home/wechat-pay/check-pay         — 查看订单是否已支付
 */

import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 订单类型（字符串，对应后端 type 字段） */
export const PayType = {
  /** 租车 */
  Car: "car",
  /** 酒店 */
  Hotel: "hotel",
  /** 旅游线路（旧） */
  Tour: "tour",
  /** 团游 */
  GroupTour: "group_tour",
  /** 导游 */
  Guide: "guide",
  /** 达人服务 */
  Expert: "expert",
} as const;

export type PayType = (typeof PayType)[keyof typeof PayType];

/** 微信JSAPI支付参数（调起收银台所需6个字段） */
export interface JsapiPayParams {
  appId: string;
  timeStamp: string;
  nonceStr: string;
  package: string; // "prepay_id=wx..."
  signType: string; // "RSA"
  paySign: string;
}

/** 统一确认支付页面 - 响应 data */
export interface OrderInfoData {
  order_no: string;
  title: string;
  amount: number;
}

/** 统一确认支付页面 - 完整响应 */
export interface OrderInfoResponse {
  code: number;
  message: string;
  data: OrderInfoData;
}

/** 发起支付 - 完整响应 */
export interface UnifiedPayResponse {
  code: number;
  message: string;
  data: JsapiPayParams;
}

/** 查看订单是否已支付 - 响应 data */
export interface CheckPayStatusData {
  order_no: string;
  title: string;
  amount: number;
}

/** 查看订单是否已支付 - 完整响应 */
export interface CheckPayStatusResponse {
  code: number;
  message: string;
  data: CheckPayStatusData;
}

// ==================== 接口请求 ====================

/**
 * 统一确认支付页面 — 获取订单信息
 * GET /home/wechat-pay/order-info
 *
 * @param orderNo 订单号（如 CR202604241640135064）
 * @param type    业务类型：car(租车) / hotel(酒店) / tour(旅游) / guide(导游)
 * @returns 订单基本信息 { order_no, title, amount }
 */
export function getOrderInfo(orderNo: string, type: PayType = PayType.Car) {
  return http.get<OrderInfoResponse>("/home/wechat-pay/order-info", {
    params: { order_no: orderNo, type },
  });
}

/**
 * 发起支付（创建JSAPI支付订单）
 * POST /home/wechat-pay/jsapi/pay （form-data）
 *
 * 详细说明：保存联系人和出行人信息到已创建的订单
 *
 * @param orderNo 订单号（如 CR20260430094533718）
 * @param type    业务类型（必填）：car(租车) / hotel(酒店) / tour(旅游) / guide(导游)
 * @returns 调起微信支付所需的6个参数 { appId, timeStamp, nonceStr, package, signType, paySign }
 */
export function jsapiCreateOrder(orderNo: string, type: PayType = PayType.Car) {
  const formData = new FormData();
  formData.append("order_no", orderNo);
  formData.append("type", type);

  return http.post<UnifiedPayResponse>(
    "/home/wechat-pay/jsapi/pay",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
}

/**
 * 查看订单是否已支付
 * GET /home/wechat-pay/check-pay
 *
 * @param orderNo 订单号（如 HT202604301149478431）
 * @param type    业务类型（必填）：car(租车) / hotel(酒店) / tour(旅游) / guide(导游)
 * @returns 订单支付状态 { order_no, title, amount }
 */
export function queryOrderStatus(orderNo: string, type: PayType = PayType.Car) {
  return http.get<CheckPayStatusResponse>("/home/wechat-pay/check-pay", {
    params: { order_no: orderNo, type },
  });
}
